import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useTableApi } from '~Components/TableUniverse';
import { useDrop } from 'react-dnd';
import PlusIcon from '~/Assets/icons/add.svg';
import SelectOptionModal from '~Components/SelectOptionModal';
import { range } from 'lodash';
import { useButton, useI18n, useLogger, useSelector } from '~/Hooks';
import itemsType from '~Scenes/Tables/Components/dragItemsType';
import { useTableContext } from '~Scenes/Handy/Context/TableContext';

const Wrapper = styled.div<{
  isOver: boolean;
  canDrop: boolean;
}>`
  background: ${(props) =>
    props.isOver ? 'var(--colorBlueGray200)' : 'var(--colorBlueGray1200)'};
  border-radius: 4px;
  height: 100%;
  max-height: 88px;
  width: 64px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  color: ${(props) => (props.isOver ? 'var(--colorBlueGray1200)' : 'var(--colorBlueGray500)')};
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: 0.24px;
  text-transform: uppercase;
  box-sizing: border-box;
  position: relative;
  z-index: ${(props) => (props.canDrop ? 3 : 'auto')};
  transition: background 0.2s ease-in-out, color 0.2s ease-in-out;
  padding: 6px 4px;
`;

const AddIcon = styled.div`
  height: 24px;
  width: 24px;
  mask-image: url(${PlusIcon});
  mask-position: center;
  mask-size: contain;
  mask-repeat: no-repeat;
  margin-bottom: 4px;
  background-color: var(--colorBlueGray600);
`;

interface Props {
  nrOfCheques: number;
  selectCheque: (id: string) => void;
  enableShowCheckAnimation: () => void;
  tableCheques: any;
  showBong: () => void;
  showLogin: () => void;
  disableCoverage?: boolean;
}

const NewCheque: React.FunctionComponent<Props> = ({
  nrOfCheques,
  selectCheque,
  enableShowCheckAnimation,
  tableCheques,
  showBong,
  showLogin,
  disableCoverage,
}) => {
  const { i18n } = useI18n();
  const { selectedTable } = useTableContext();

  const staffId = useSelector((state) =>
    state.companyStaff?.staffs.length ? state.staff : false,
  );
  const deviceWakeState = useSelector((state) => state.deviceWakeState);

  const [showModal, setShowModal] = useState(false);
  const [firstCheque, setFirstCheque] = useState(false);

  const logger = useLogger();
  const api = useTableApi();

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [itemsType.ITEM],
    drop: (data) => {
      //@ts-ignore
      api.moveItemsToNewCheque(data.selectedItems, selectedTable.id);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  useEffect(() => {
    if (firstCheque) {
      showBong();
      setFirstCheque(false);
    }
  }, [firstCheque]);

  const handleCoverage = (count: number) => {
    logger.info('Creating new cheque');
    createCheque(count);
    setShowModal(false);
  };

  const handleClick = () => {
    if (staffId === null) {
      showLogin();
      return;
    }

    /**
     * It's most likely QSR mode, so we don't want to show the coverage modal.
     */
    if (disableCoverage) {
      createCheque(1);
      return;
    }

    if (nrOfCheques < 1) {
      setShowModal(true);
    } else {
      const coverage = Math.max(
        ...tableCheques.map((item: any) => (item.coverage ? item.coverage : 0)),
      );

      logger.info('Creating new cheque');

      createCheque(coverage);
    }
  };

  const newChequeButton = useButton({
    onClick: handleClick,
    disabled: deviceWakeState.syncingTableEvents,
    disabledToast: i18n('Handy.Tables.LoadingEvents'),
  });

  if (!selectedTable) {
    return null;
  }

  const createCheque = (coverage: number) => {
    const id = api.createCheque(selectedTable?.id, coverage);
    api.log.createCheque(id, selectedTable.id, coverage);
    selectCheque(id);
    enableShowCheckAnimation();
    if (nrOfCheques === 0) {
      setFirstCheque(true);
    }
  };

  return (
    <>
      <Wrapper {...newChequeButton} ref={drop} canDrop={canDrop} isOver={isOver}>
        <AddIcon />
        {i18n('TM.New')}
      </Wrapper>

      {showModal && (
        <SelectOptionModal
          enableCustomInput
          title={i18n('TM.Coverage')}
          options={range(1, 8)}
          onSelect={(count) => {
            handleCoverage(count as number);
          }}
          onCancel={() => setShowModal(false)}
        />
      )}
    </>
  );
};

export default NewCheque;
